import React, { useState } from "react";
import { toast } from "react-toastify";
import Cookies from "js-cookie";
import { adminMainUrl } from "../../../../Config";
import { useSelector } from "react-redux";

const LedgerAccountBasicInfoForm = ({
  ledger_account_id,
  ledgerFormData,
  onValueChange,
  saveAndNext,
  tabId,
}) => {
  const {
    GroupMasterName,
    loadingGroupMasterName,
    MasterAccountGroupName,
    loadingMasterAccountGroupName,
  } = useSelector((state) => state?.MasterReducer);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = () => {
    const requiredFields = {
      ledger_type: "Ledger Type",
      ledger_gst: "GST",
      ledger_party_name: "Party Name",
      ledger_group: "Group Name",
      ledger_account_group: "Account Group",
      ledger_debit_or_credit: "Debit/Credit",
    };

    for (const [field, label] of Object.entries(requiredFields)) {
      if (!ledgerFormData[field] || ledgerFormData[field] === "") {
        toast.error(`${label} is required`);
        return false;
      }
    }
    return true;
  };

  const submitLedgerForm = async () => {
    // Step 1: Client-side validation
    
    if (!validateForm()) return;

    setIsSubmitting(true);

    const {
      ledger_code,
      ledger_type,
      ledger_gst,
      ledger_party_name,
      ledger_group,
      ledger_sales_person,
      ledger_account_group,
      ledger_credit_limit,
      ledger_opening_balance,
      ledger_debit_or_credit,
      ledger_credit_day,
    } = ledgerFormData;

    try {
      const response = await fetch(
        `${adminMainUrl}/api/account/ledger-account`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "auth-token": Cookies.get("token"),
            user_id: Cookies.get("flexichain_user_id"),
          },
          body: JSON.stringify({
            ledger_code,
            ledger_gst,
            ledger_type,
            ledger_party_name,
            ledger_group,
            ledger_sales_person,
            ledger_account_group,
            ledger_credit_limit: Number(ledger_credit_limit) || 0,
            ledger_opening_balance: Number(ledger_opening_balance) || 0,
            ledger_debit_or_credit,
            ledger_credit_day: Number(ledger_credit_day) || 0,
          }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        toast.success(data?.msg || "Ledger account created successfully");

        if (data?.ledger_account_id) {
          localStorage.setItem("ledger_account_id", data.ledger_account_id);
        }

        saveAndNext(tabId);
      } else {
        toast.error(data?.msg || "Failed to create ledger account");
        console.error("Server error:", data);
      }
    } catch (error) {
      console.error("Network/API error:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <style>{`
        .padding-pt-9 {
          padding: 10px;
        }
        .form-control:disabled {
          background-color: #e9ecef !important;
          opacity: 1;
        }
      `}</style>

      <div className="row">
        {/* Ledger Code */}
        <div className="col-md-6 mb-4">
          <label className="form-label fw-medium">
            Ledger Code <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            className="form-control bg-light"
            placeholder="Auto-Generated"
            disabled
            value={ledgerFormData?.ledger_code || ""}
          />
        </div>

        {/* Party Name */}
        <div className="col-md-6 mb-4">
          <label className="form-label fw-medium">
            Party Name <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            name="ledger_party_name"
            className="form-control bg-light"
            placeholder="Enter Party Name"
            onChange={onValueChange}
            value={ledgerFormData.ledger_party_name || ""}
            required
          />
        </div>

        {/* Ledger Group */}
        <div className="col-md-6 mb-4">
          <label className="form-label fw-medium">
            Group Name <span className="text-danger">*</span>
          </label>
          <select
            name="ledger_group"
            className="form-select bg-light padding-pt-9"
            onChange={onValueChange}
            value={ledgerFormData.ledger_group || ""}
            disabled={loadingGroupMasterName}
            required
          >
            <option value="">Select group</option>
            {GroupMasterName?.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </div>

        {/* Sales Person */}
        <div className="col-md-6 mb-4">
          <label className="form-label fw-medium">Sales Person</label>
          <input
            type="text"
            name="ledger_sales_person"
            className="form-control bg-light"
            placeholder="Enter Sales Person Name"
            onChange={onValueChange}
            value={ledgerFormData.ledger_sales_person || ""}
            required
          />
        </div>

        {/* Account Group */}
        <div className="col-md-6 mb-4">
          <label className="form-label fw-medium">
            Account Group <span className="text-danger">*</span>
          </label>
          <select
            name="ledger_account_group"
            className="form-select bg-light padding-pt-9"
            onChange={onValueChange}
            value={ledgerFormData.ledger_account_group || ""}
            disabled={loadingMasterAccountGroupName}
            required
          >
            <option value="">Select account group</option>
            {MasterAccountGroupName?.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </div>

        {/* Opening Balance */}
        <div className="col-md-6 mb-4">
          <label className="form-label fw-medium">Opening Balance</label>
          <input
            type="number"
            name="ledger_opening_balance"
            className="form-control bg-light"
            placeholder="0"
            onChange={onValueChange}
            value={ledgerFormData.ledger_opening_balance || ""}
            step="1"
          />
        </div>

        {/* Credit Limit */}
        <div className="col-md-6 mb-4">
          <label className="form-label fw-medium">Credit Limit</label>
          <input
            type="number"
            name="ledger_credit_limit"
            className="form-control bg-light"
            placeholder="0"
            onChange={onValueChange}
            value={ledgerFormData.ledger_credit_limit || ""}
            min="1"
            step="1"
          />
        </div>

        {/* Credit Days */}
        <div className="col-md-6 mb-4">
          <label className="form-label fw-medium">Credit Days</label>
          <input
            type="number"
            name="ledger_credit_day"
            className="form-control bg-light"
            placeholder="0"
            onChange={onValueChange}
            value={ledgerFormData.ledger_credit_day || ""}
            min="1"
          />
        </div>

        {/* Debit or Credit */}
        <div className="col-md-6 mb-4">
          <label className="form-label fw-medium">
            DR/CR <span className="text-danger">*</span>
          </label>
          <select
            name="ledger_debit_or_credit"
            className="form-select bg-light"
            onChange={onValueChange}
            value={ledgerFormData.ledger_debit_or_credit || ""}
            required
          >
            <option value="">Select</option>
            <option value="DEBIT">DEBIT</option>
            <option value="CREDIT">CREDIT</option>
          </select>
        </div>

        {/* Action Buttons */}
        <div className="d-flex gap-2 mt-4">
          <button
            type="button"
            onClick={submitLedgerForm}
            className="btn btn-primary px-4"
            disabled={isSubmitting}
          >
            <i className="fas fa-save me-1"></i>
            {isSubmitting ? "Saving..." : "Save & Next"}
          </button>
        </div>
      </div>
    </>
  );
};

export default LedgerAccountBasicInfoForm;
